<template>
  <NuxtLink
    v-element-visibility="scrolledIntoView"
    :to="campgroundLink"
    target="_blank"
    :class="
      [
        'relative overflow-hidden block space-y-3 text-primary-950! no-underline!',
        {
          'animate-pulse cursor-progress': isStillLoading,
          'cursor-pointer': !isStillLoading,
          'bg-white rounded-lg shadow-md': variant === 'map',
        },
      ]"
   
    @click="campgroundClickHandler"
  >
    <div
      :class="['relative block aspect-3/2 overflow-hidden', {
        'rounded-lg': variant !== 'map',
        'rounded-t-lg': variant === 'map',
      }]"
    >
      <AtomTransition timing="fast">
        <TSkeleton
          v-if="isStillLoading"
          key="loader"
          fill
        />
        <TSwipeImages
          v-else
          key="content"
          :contents="photos"
          @content-viewed="updateImageIndex"
        />
      </AtomTransition>

      <AtomTransition timing="instant">
        <button
          v-if="!isStillLoading && hasCloseButton"
          class="absolute top-2 left-2 z-3 size-8 rounded-full bg-white/75 flex items-center justify-center cursor-pointer"
          type="button"
         
          @click.stop.prevent="emit('close')"
        >
          <TIcon :icon="['far', 'xmark']" />
        </button>
      </AtomTransition>
    </div>

    <div :class="{ 'px-3 pb-3': variant === 'map' }">
      <div class="relative h-6 mb-1">
        <AtomTransition timing="fast">
          <TSkeleton
            v-if="isStillLoading"
            key="loader"
            class="h-6 w-full"
          />
          <div
            v-else
            class="w-full truncate h-6 font-semibold"
          >
            {{ campground?.Name }}
          </div>
        </AtomTransition>
      </div>

      <div class="relative h-5 text-gray-500 text-sm mb-2">
        <AtomTransition timing="fast">
          <TSkeleton
            v-if="isStillLoading"
            key="loader"
            class="h-5 w-full"
          />
          <p
            v-else
            key="content"
            class="truncate"
          >
            {{ computedPlaceName }}
            <template v-if="distanceAway">
              ({{ t('away', { distance: distanceAway }) }})
            </template>
          </p>
        </AtomTransition>
      </div>

      <div class="relative h-6 mb-2">
        <AtomTransition timing="fast">
          <TSkeleton
            v-if="isStillLoading"
            key="loader"
            class="h-6 w-1/2"
          />
          <div
            v-else
            key="content"
          >
            <div class="flex items-center gap-2">
              <CampgroundsAveragePrice :average-price="averagePrice" />

              <div v-if="campground?.NumberOfSites">
                ({{ t('sites', campground.NumberOfSites) }})
              </div>
            </div>
          </div>
        </AtomTransition>
      </div>

      <div class="relative h-6 mb-2">
        <AtomTransition timing="fast">
          <TSkeleton
            v-if="isStillLoading"
            key="loader"
            class="h-6 w-1/2"
          />
          <div
            v-else
            key="content"
          >
            <CampgroundsAmenitiesPreview :amenities="campground?.Amenities ?? []" />
          </div>
        </AtomTransition>
      </div>

      <div
        v-if="showDelivery"
        class="h-12 mt-4"
      >
        <AtomTransition timing="fast">
          <TSkeleton
            v-if="isStillLoading"
            key="loader"
            class="h-12 w-3/4"
          />
          <LazyEstimateAddDelivery
            v-else
            key="content"
            :destination="destinationData"
            @click="addDeliveryClickHandler"
          />
        </AtomTransition>
      </div>
    </div>
  </NuxtLink>
</template>

<script setup lang="ts">
import { vElementVisibility } from '@vueuse/components'
import { type COUNTRY_SHORTS, ROUTE_BASE_NAME } from '~/constants'
import type { BookingEstimateDestination } from '~/types'
import type { Campground } from '~/types/rental-api-aliases'
import type { SwipeImage } from '~/components/_tailwind/swipe/types'

const props = withDefaults(defineProps<{
  /**
   * The actual index of the item in the list. This is used to emit back to the
   * list in order to tell the list when to load the next set of items.
   */
  index?: number
  campground?: Campground
  isLoading?: boolean
  showDelivery?: boolean
  noLazyLoad?: boolean
  eagerLoad?: boolean
  variant?: 'gallery' | 'map'
  hasCloseButton?: boolean
}>(), {
  index: 0,
  variant: 'gallery',
})

const emit = defineEmits<{
  'scrolled-into-view': [index: number]
  'close': []
}>()

const { t, locale } = useI18n()
const { routeBaseName } = useBaseName()
const { getDynamicImage, presets } = useImage()

const imageIndex = ref(0)
/**
 * Tracks wether or not the card is scrolled into view.
 */
const isVisible = ref(props.noLazyLoad)

/**
 * The card can be in 2 loading "states":
 * 1. The data is actually still loading from the API.
 * 2. The card hasn't scrolled into view yet (if enabled).
 */
const isStillLoading = computed(() => props.isLoading || !isVisible.value)

const distanceAway = computed(() => {
  if (!props.campground?.DistanceKm) {
    return ''
  }

  const unit = countryCodeToDistanceUnit(props.campground.CountryCode as COUNTRY_SHORTS)

  return formatDistance(props.campground.DistanceKm, unit, locale.value, 0)
})

const preset = presets.LANDSCAPE_SM

const photos = computed(() => {
  if (!props.campground?.Photos) {
    return []
  }

  const images: SwipeImage[] = props.campground.Photos.map((path, index) => {
    return {
      type: 'image' as const,
      path: path,
      src: getDynamicImage({ path, preset }) ?? '',
      description: props.campground?.Name ?? '',
      width: preset.width,
      height: preset.height,
      loading: props.eagerLoad && index === 0 ? 'eager' : 'lazy',
    }
  })

  return images
})

const computedPlaceName = computed(() => {
  if (props.campground?.City) {
    return [props.campground?.City, props.campground?.RegionCode].filter(Boolean).join(', ')
  }

  return props.campground?.Region
})

const averagePrice = computed(() => getCampgroundAveragePrice(props.campground?.NightlyRates))

const localePath = useLocalePath()

const campgroundLink = computed(() => {
  // Early exit if we don't have a campground id with the based campground link.
  if (!props.campground?.Id) {
    return localePath(ROUTE_BASE_NAME.campgrounds)
  }

  return localePath({ name: ROUTE_BASE_NAME.rv_rentals_campground, params: { campgroundSlug: props.campground.Id } })
})

onBeforeMount(() => {
  /**
   * Determine wether or not we want to only show data when the card is
   * scrolled into view.
   */
  isVisible.value = props.noLazyLoad
})

function scrolledIntoView(visible: boolean) {
  /**
   * If we don't want to lazy load content, or when the card has scrolled
   * out of view, exit early.
   *
   * Otherwise fire the 'scrolled-into-view' event and track it locally.
   */
  if (props.noLazyLoad || !visible) return

  emit('scrolled-into-view', props.index)

  isVisible.value = visible
}

function updateImageIndex(index: number) {
  imageIndex.value = index
}

const destinationData = computed(() => ({
  latitude: props.campground?.Latitude,
  longitude: props.campground?.Longitude,
  types: [
    'campground',
    'establishment',
    'lodging',
    'park',
    'point_of_interest',
    'rv_park',
  ],
  name: props.campground?.Name ?? undefined,
  placeId: '', // PlaceId is not Google PlaceId on Campground API
  country: props.campground?.CountryCode as COUNTRY_SHORTS,
} as BookingEstimateDestination))

function addDeliveryClickHandler() {
  if (isStillLoading.value) return

  trackCTAClicked({
    pageSource: routeBaseName.value,
    cta: 'addDelivery',
  })
}

function campgroundClickHandler(e: Event) {
  const cta = e.target instanceof HTMLImageElement ? 'carouselImage' : 'card'

  trackExternalLinkClicked({
    pageSource: routeBaseName.value,
    cta,
    externalLink: campgroundLink.value,
  })
}
</script>

<i18n lang="json">
{
  "en": {
    "away": "{distance} away",
    "sites": "sites | 1 site | {n} sites"
  },
  "fr": {
    "away": "à {distance}",
    "sites": "site | 1 site | {n} sites"
  }
}
</i18n>
